import { useState, useEffect, useRef } from 'react'
import { batchAPI, evaluationAPI } from '../../services/api'

const DIMENSION_LABELS = {
  coherence: 'Coherence',
  clinical_relevance: 'Clinical Relevance',
  persona_fidelity: 'Persona Fidelity',
  context_adherence: 'Context Adherence',
  naturalness: 'Naturalness',
  episode_completeness: 'Episode Completeness',
}

export default function BatchEvaluationLLMPanel({ selectedJourneyIds = [] }) {
  const [jobId, setJobId] = useState(null)
  const [jobStatus, setJobStatus] = useState(null)
  const [error, setError] = useState(null)
  const pollRef = useRef(null)

  const startEvaluation = async () => {
    if (selectedJourneyIds.length === 0) return
    setError(null)
    try {
      const result = await batchAPI.startEvaluation({
        journey_ids: selectedJourneyIds,
        sample_episodes: true,
        judge_model: 'gpt-4.1-nano',
      })
      if (result.error) {
        setError(result.error)
        return
      }
      setJobId(result.job_id)
      setJobStatus({ status: 'pending', progress: { completed: 0, total: selectedJourneyIds.length } })
      startPolling(result.job_id)
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start evaluation')
    }
  }

  const startPolling = (id) => {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = setInterval(async () => {
      try {
        const job = await batchAPI.getJob(id)
        setJobStatus(job)
        if (['completed', 'failed', 'cancelled'].includes(job.status)) {
          clearInterval(pollRef.current)
          pollRef.current = null
        }
      } catch {
        // Ignore
      }
    }, 3000)
  }

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [])

  const handleExport = async (format) => {
    try {
      await evaluationAPI.exportResults(selectedJourneyIds, format)
    } catch {
      // Ignore
    }
  }

  const isRunning = jobStatus?.status === 'running' || jobStatus?.status === 'pending'
  const isDone = jobStatus?.status === 'completed' || jobStatus?.status === 'failed'
  const results = jobStatus?.results_summary
  const progress = jobStatus?.progress || {}

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <div className="flex items-center justify-between">
          <h3 className="card-title text-lg">LLM-Judge Batch Evaluation</h3>
          {isDone && results && (
            <div className="dropdown dropdown-end">
              <label tabIndex={0} className="btn btn-outline btn-sm">Export Results</label>
              <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-36">
                <li><a onClick={() => handleExport('json')}>JSON</a></li>
                <li><a onClick={() => handleExport('csv')}>CSV</a></li>
              </ul>
            </div>
          )}
        </div>

        {!jobId && (
          <div className="space-y-3">
            <p className="text-sm text-base-content/60">
              {selectedJourneyIds.length === 0
                ? 'Select journeys above to evaluate with LLM-Judge.'
                : `${selectedJourneyIds.length} journey(s) selected for evaluation.`}
            </p>
            {error && <div className="alert alert-error text-sm"><span>{error}</span></div>}
            <button
              className="btn btn-primary btn-sm"
              disabled={selectedJourneyIds.length === 0}
              onClick={startEvaluation}
            >
              Run LLM-Judge Evaluation
            </button>
          </div>
        )}

        {jobId && isRunning && (
          <div className="space-y-2 mt-2">
            <div className="flex items-center gap-2">
              <span className="loading loading-spinner loading-sm"></span>
              <span className="text-sm">Evaluating journeys...</span>
            </div>
            <progress
              className="progress progress-info w-full"
              value={progress.completed || 0}
              max={progress.total || 1}
            ></progress>
            <div className="text-xs text-base-content/50">
              {progress.completed || 0} / {progress.total || 0} journeys evaluated
            </div>
          </div>
        )}

        {isDone && results && (
          <div className="space-y-4 mt-2">
            <div className="flex items-center gap-3">
              <span className={`badge ${jobStatus.status === 'completed' ? 'badge-success' : 'badge-error'}`}>
                {jobStatus.status}
              </span>
              <span className="text-sm">
                {results.completed}/{results.total} journeys evaluated
                {results.failed > 0 && <span className="text-error"> ({results.failed} failed)</span>}
              </span>
            </div>

            {/* Aggregate dimension means */}
            {results.dimension_means && (
              <div>
                <h4 className="text-sm font-semibold mb-2">Aggregate Scores (mean across all journeys)</h4>
                <div className="bg-base-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium">Overall:</span>
                    <span className={`badge badge-lg ${results.overall_mean >= 4 ? 'badge-success' : results.overall_mean >= 3 ? 'badge-warning' : 'badge-error'}`}>
                      {results.overall_mean?.toFixed(2)} / 5.00
                    </span>
                  </div>
                  <div className="space-y-1">
                    {Object.entries(results.dimension_means).map(([dim, score]) => (
                      <div key={dim} className="flex items-center gap-2">
                        <span className="text-xs w-36 text-right">{DIMENSION_LABELS[dim] || dim}</span>
                        <progress
                          className={`progress w-full ${score >= 4 ? 'progress-success' : score >= 3 ? 'progress-warning' : 'progress-error'}`}
                          value={score}
                          max="5"
                        ></progress>
                        <span className="text-xs w-10 text-right font-mono">{score?.toFixed(1)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Per-journey table */}
            {results.per_journey && results.per_journey.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2">Per-Journey Results</h4>
                <div className="overflow-x-auto">
                  <table className="table table-xs">
                    <thead>
                      <tr>
                        <th>Journey</th>
                        <th>Overall</th>
                        {Object.keys(DIMENSION_LABELS).map(dim => (
                          <th key={dim} className="text-xs">{DIMENSION_LABELS[dim].split(' ')[0]}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {results.per_journey.map((r, idx) => (
                        <tr key={idx}>
                          <td className="text-xs font-mono">{r.journey_id?.slice(0, 8)}...</td>
                          <td>
                            <span className={`badge badge-xs ${r.overall_score >= 4 ? 'badge-success' : r.overall_score >= 3 ? 'badge-warning' : 'badge-error'}`}>
                              {r.overall_score?.toFixed(1)}
                            </span>
                          </td>
                          {Object.keys(DIMENSION_LABELS).map(dim => (
                            <td key={dim} className="text-xs">
                              {r.dimension_rollups?.[dim]?.mean?.toFixed(1) || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <button
              className="btn btn-outline btn-sm"
              onClick={() => { setJobId(null); setJobStatus(null) }}
            >
              New Evaluation
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
